import { FC, useState } from 'react';
import { Button, Card, RadixTable } from '@features/ui';
import {
  periodOptions,
  mockNewAffiliatePayoutByUsers,
} from '@features/admin/reports/reportData';
import { TReportAffiliatePayoutData, ReportPeriod } from '@src/types';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

export const NewAffiliatePayoutAmountByUsers: FC = () => {
  // Data state
  const [data] = useState<TReportAffiliatePayoutData[]>(
    mockNewAffiliatePayoutByUsers
  );
  const [minimumPayout, setMinimumPayout] = useState<string>('0');

  // Use custom hooks for filter management (period only)
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_12_MONTHS,
    enableComparisons: false,
  });

  // Filter data by minimum payout
  const filteredData = data.filter(
    (item) => item.payout >= parseFloat(minimumPayout || '0')
  );

  // Calculate total payout
  const totalPayout = filteredData.reduce((sum, item) => sum + item.payout, 0);

  // Use custom hook for table columns - simple table with userName and payout
  const { columns, tableData } =
    useReportTableColumns<TReportAffiliatePayoutData>({
      data: filteredData,
      getAccessField: (item) => item.userName,
      getMainDataField: () => 0,
      includeTotalsRow: true,
      totalsRowLabel: 'Total',
      summaryRow: { payout: totalPayout },
      customColumns: [
        {
          title: 'PAYOUT',
          className: 'text-right',
          render: (item) => {
            if (!item) return null;

            const isTotalsRow = item.userName === 'Total';
            const value = isTotalsRow ? totalPayout : item.payout;

            return (
              <p className={isTotalsRow ? 'text-[#B6D72C]' : 'text-white'}>
                ${value.toFixed(2)}
              </p>
            );
          },
        },
      ],
    });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // When API is ready:
      // const params: TReportAffiliatePayoutParams = {
      //   period: filters.selectedPeriod as ReportPeriod,
      //   start_date: filters.startDate?.toISOString() || null,
      //   end_date: filters.endDate?.toISOString() || null,
      //   minimum_payout: parseFloat(minimumPayout || '0'),
      // };
      // const response = await getNewAffiliatePayoutByUsers(params);
      // setData(response.data);

      console.log('Running report with period:', filters.selectedPeriod);
      console.log('Minimum payout:', minimumPayout);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        New Affiliate Payout Amount by Users
      </h1>

      <Card className="p-6">
        <div className="space-y-4">
          <ReportFilteringSettings
            dateRangeValue={filters.valueStart}
            isLoading={filters.isLoading}
            periodOptions={periodOptions}
            periodRange={filters.periodRange}
            selectedPeriod={filters.selectedPeriod}
            showGroupByFilter={false}
            showRunReportButton={false}
            onDateRangeChange={filters.setValueStart}
            onPeriodChange={filters.setSelectedPeriod}
          />

          {/* Custom Minimum Payout Field */}
          <div className="flex items-center">
            <div className="min-w-[250px]">
              <label className="text-white text-sm font-medium">
                Minimum Payout
              </label>
              <p className="text-[#999999] text-xs mt-1">
                include to report only affiliates <br />
                with payout in given period <br />
                more or equal to
              </p>
            </div>
            <div className="flex-1 flex items-center gap-2">
              <input
                className="px-3 py-2 border rounded-md text-white text-sm w-[300px] max-w-[300px]"
                max="999999"
                min="0"
                placeholder="0.00"
                step="0.01"
                type="number"
                value={minimumPayout}
                onChange={(e) => setMinimumPayout(e.target.value)}
              />
              <span className="text-[#999999] text-sm">USD</span>
            </div>
          </div>
          <div className="flex items-center">
            <div className="min-w-[250px]" />

            <div className="flex justify-end">
              <Button
                className="bg-[#B6D72C] hover:bg-[#A5C628] text-black font-semibold px-8"
                disabled={filters.isLoading}
                onClick={handleRunReport}
              >
                {filters.isLoading ? 'Loading...' : 'Run Report'}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-white text-sm">
              Showing {filteredData.length} users with payout ≥ ${minimumPayout}
            </p>
          </div>
          <RadixTable columns={columns} data={tableData} />
        </div>
      </Card>
    </div>
  );
};
